import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useAuth } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Globe, AlertTriangle, KeyRound, ShieldCheck } from 'lucide-react'
import { getAuthMode } from '@/lib/env'
import gridxLogo from '@assets/logos/gridx.png'

export default function Login() {
  const [apiKey, setApiKey] = useState('')
  const [apiType, setApiType] = useState<'bearer' | 'token'>('bearer')
  const [showApiKeyMode, setShowApiKeyMode] = useState(false)

  const { login, loading, error, environment, setEnvironment } = useAuthStore()
  const auth = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()

  const runtimeAuthMode = getAuthMode()

  // Load auth mode preference from localStorage
  useEffect(() => {
    const preference = localStorage.getItem('auth_mode_preference')
    if (preference === 'API_KEY') {
      setShowApiKeyMode(true)
    } else {
      setShowApiKeyMode(false)
    }
  }, [])

  const handleEnvironmentChange = async (env: 'staging' | 'production') => {
    await setEnvironment(env)
  }

  const handleAuth0Login = () => {
    if (auth.loginWithRedirect) {
      auth.loginWithRedirect()
    }
  }

  const handleSwitchToApiKey = () => {
    setShowApiKeyMode(true)
    localStorage.setItem('auth_mode_preference', 'API_KEY')
  }

  const handleSwitchToAuth0 = () => {
    setShowApiKeyMode(false)
    localStorage.setItem('auth_mode_preference', 'AUTH0')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!apiKey.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter an API key',
        variant: 'destructive',
      })
      return
    }

    const success = await login(apiKey, apiType)

    if (success) {
      toast({
        title: 'Success',
        description: 'Successfully logged in',
      })
      navigate('/')
    } else {
      toast({
        title: 'Login Failed',
        description: error || 'Invalid API key',
        variant: 'destructive',
      })
    }
  }

  // Render Auth0 login UI
  const renderAuth0Login = () => (
    <>
      <CardDescription>
        Sign in with your gridX account to access the flexibility pool management system
      </CardDescription>
      <div className="space-y-4 mt-6">
        <Button
          onClick={handleAuth0Login}
          className="w-full"
          size="lg"
          disabled={auth.isLoading}
        >
          {auth.isLoading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Loading...
            </>
          ) : (
            <>
              <ShieldCheck className="mr-2 h-5 w-5" />
              Login with Auth0
            </>
          )}
        </Button>

        {auth.error && (
          <div className="rounded-md bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 p-3">
            <p className="text-sm text-red-800 dark:text-red-300">
              {auth.error.message}
            </p>
          </div>
        )}

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              Or
            </span>
          </div>
        </div>

        <Button
          variant="ghost"
          onClick={handleSwitchToApiKey}
          className="w-full"
          type="button"
        >
          <KeyRound className="mr-2 h-4 w-4" />
          Use API Key Instead
        </Button>
      </div>
    </>
  )

  // Render API Key login UI
  const renderApiKeyLogin = () => (
    <>
      <CardDescription>
        Enter your API key to access the flexibility pool management system
      </CardDescription>
      <form onSubmit={handleSubmit} className="space-y-4 mt-6">
        {/* Environment Selector */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Environment
          </Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={environment === 'staging' ? 'default' : 'outline'}
              onClick={() => handleEnvironmentChange('staging')}
              disabled={loading}
              className="flex-1"
            >
              Staging
            </Button>
            <Button
              type="button"
              variant={environment === 'production' ? 'default' : 'outline'}
              onClick={() => handleEnvironmentChange('production')}
              disabled={loading}
              className="flex-1"
            >
              Production
            </Button>
          </div>
          <p className="text-xs text-muted-foreground font-mono">
            {environment === 'production'
              ? 'https://api.gridx.de'
              : 'https://api.staging.gridx.de'}
          </p>
          {environment === 'production' && (
            <div className="flex items-center gap-2 rounded-md bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 p-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <p className="text-xs text-amber-800 dark:text-amber-300">
                Production environment - changes will affect real systems
              </p>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="apiKey">API Key</Label>
          <Input
            id="apiKey"
            type="password"
            placeholder="Enter your API key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="apiType">Authentication Type</Label>
          <select
            id="apiType"
            value={apiType}
            onChange={(e) => setApiType(e.target.value as 'bearer' | 'token')}
            disabled={loading}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="bearer">Bearer (JWT)</option>
            <option value="token">Token (API Key)</option>
          </select>
        </div>

        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Logging in...
            </>
          ) : (
            'Login'
          )}
        </Button>

        {runtimeAuthMode === 'AUTH0' && (
          <>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or
                </span>
              </div>
            </div>

            <Button
              variant="ghost"
              onClick={handleSwitchToAuth0}
              className="w-full"
              type="button"
            >
              <ShieldCheck className="mr-2 h-4 w-4" />
              Use Auth0 Instead
            </Button>
          </>
        )}
      </form>
    </>
  )

  // Determine which mode to show
  const shouldShowAuth0 = runtimeAuthMode === 'AUTH0' && !showApiKeyMode

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle><img src={gridxLogo} alt="gridX" className="h-10 w-auto" /></CardTitle>
          {shouldShowAuth0 ? renderAuth0Login() : renderApiKeyLogin()}
        </CardHeader>
        <CardContent>
          {/* Mode indicator */}
          <div className="mt-4 pt-4 border-t">
            <p className="text-xs text-center text-muted-foreground">
              {shouldShowAuth0 ? 'Auth0 Authentication Mode' : 'API Key Authentication Mode'}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
